import { useCallback, useEffect, useRef, useState } from "react";
import { Swords, Send, Check, ChevronDown } from "lucide-react";
import { useCategories } from "@/context/CategoriesContext";
import { gameRequest, gameError } from "./gameApi";

const resultText = (row, me) => {
  const received = row.to_user_id === me;
  const outcome = received ? row.outcome : row.outcome === "won" ? "lost" : row.outcome === "lost" ? "won" : "draw";
  return `${outcome === "won" ? "Gewonnen" : outcome === "lost" ? "Verloren" : "Gelijkspel"} · ${received ? row.from_name : row.to_name} · week ${row.iso_week}`;
};

const ChallengeResult = ({ row, user, prefix, seen }) => {
  const ref = useRef(null);
  useEffect(() => {
    let timer;
    const observer = new IntersectionObserver(([entry]) => {
      clearTimeout(timer);
      if (entry.isIntersecting) timer = setTimeout(() => seen(row.id), 10000);
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [row.id, seen]);
  return <div ref={ref} className="game-challenge flex flex-wrap items-center justify-between gap-3" data-testid={`${prefix}-result-${row.id}`}>
    <p className="text-sm text-tw-yellow">{resultText(row, user.id)}</p>
    <button type="button" className="game-secondary" onClick={() => seen(row.id)} data-testid={`${prefix}-seen-${row.id}`}><Check size={14} />Gezien</button>
  </div>;
};

export const ChallengeNotices = ({ data, user, reload, prefix = "game-challenges" }) => {
  const [error, setError] = useState("");
  const seen = useCallback(async (id) => {
    try {
      const result = await gameRequest(`/game/challenges/${id}/seen`, {}, "POST");
      if (!result.ok) throw new Error();
      setError(""); reload();
    } catch (e) { setError(gameError(e)); }
  }, [reload]);
  if (!data || (!data.received?.length && !data.results?.length)) return null;
  return <section className="my-5" data-testid={prefix}>
    {data.received?.map(row => <div className="game-challenge game-challenge-received" data-testid={`${prefix}-received-${row.id}`} key={row.id}>
      <p className="flex gap-2 text-tw-yellow text-xs font-mono uppercase tracking-widest mb-2"><Swords size={15} />Uitdaging</p>
      <p className="text-sm" data-testid={`${prefix}-text-${row.id}`}>{row.from_name} daagt je uit{row.category_name ? ` op ${row.category_name}` : ""}, nog tot zondag.</p>
      {row.message && <p className="text-sm text-tw-muted mt-2 whitespace-pre-wrap" data-testid={`${prefix}-message-${row.id}`}>{row.message}</p>}
    </div>)}
    {data.results?.map(row => <ChallengeResult key={row.id} row={row} user={user} prefix={prefix} seen={seen} />)}
    {error && <p role="alert" className="text-tw-red text-sm" data-testid={`${prefix}-error`}>{error}</p>}
  </section>;
};

export const ChallengePanel = ({ game, user, prefix = "game-challenge-panel" }) => {
  const { names } = useCategories();
  const [to, setTo] = useState("");
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const targets = game.week?.standings?.filter(row => !row.is_me) || [];
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(""); setSuccess(false);
    try {
      const result = await gameRequest("/game/challenge", { to_user_id: to, category_name: category || null, message: message || null }, "POST");
      if (!result.ok) { setError(result.detail); return; }
      setSuccess(true); setMessage(""); setTo(""); game.reload();
    } catch (e) { setError(gameError(e)); } finally { setBusy(false); }
  };
  if (user.role === "lezer" || !targets.length) return null;
  return <section className="game-standing" data-testid={prefix}>
    <h2 className="font-display text-base md:text-lg font-bold flex items-center gap-2 mb-4"><Swords size={18} className="text-tw-red" />Daag een collega uit</h2>
    <form onSubmit={submit} className="grid gap-4" data-testid={`${prefix}-form`}>
      <label className="game-field-label">Collega<select required className="game-field" value={to} onChange={e => setTo(e.target.value)} data-testid={`${prefix}-target`}><option value="">Kies een collega</option>{targets.map(row => <option key={row.user_id} value={row.user_id} data-testid={`${prefix}-target-${row.user_id}`}>{row.name}</option>)}</select></label>
      <label className="game-field-label">Categorie<select className="game-field" value={category} onChange={e => setCategory(e.target.value)} data-testid={`${prefix}-category`}><option value="">Alle categorieën</option>{names.map((name, i) => <option key={name} value={name} data-testid={`${prefix}-category-${i}`}>{name}</option>)}</select></label>
      <label className="game-field-label">Bericht (niet verplicht)<textarea rows={2} maxLength={140} className="game-field resize-y" value={message} onChange={e => setMessage(e.target.value)} data-testid={`${prefix}-message`} /></label>
      <div className="flex items-center justify-between gap-3"><span className="text-xs text-tw-muted" data-testid={`${prefix}-length`}>{message.length}/140</span><button disabled={busy || !to} className="game-primary" data-testid={`${prefix}-send`}><Send size={14} />{busy ? "Versturen…" : "Daag uit"}</button></div>
      {error && <p className="text-sm text-tw-red" role="alert" data-testid={`${prefix}-error`}>{error}</p>}
      {success && <p className="text-sm text-tw-yellow" role="status" data-testid={`${prefix}-success`}>Uitdaging verstuurd.</p>}
    </form>
    {game.challenges?.sent?.map(row => <p className="game-challenge text-sm text-tw-muted" key={row.id} data-testid={`${prefix}-sent-${row.id}`}>Verstuurd aan {row.to_name}{row.category_name ? ` · ${row.category_name}` : ""}</p>)}
    {!!game.challenges?.history?.length && <details className="mt-5" data-testid={`${prefix}-history`}><summary className="cursor-pointer text-xs text-tw-muted flex items-center gap-2" data-testid={`${prefix}-history-toggle`}><ChevronDown size={14} />Eerdere uitdagingen</summary>{game.challenges.history.map(row => <p className="game-challenge text-sm" key={row.id} data-testid={`${prefix}-history-${row.id}`}>{resultText(row, user.id)}</p>)}</details>}
  </section>;
};